import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { Dropdownlist } from '@/components/Dropdownlist';
import { ConfirmModal } from '@/components/ConfirmModal';

import { App } from '@/services/app-compat';
import { Codes } from '@/constants/codes';

const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const getInsecureUUID = _wg('getInsecureUUID');
const scrollToLastMobileCard = _wg('scrollToLastMobileCard');

interface FieldInfo {
  label: string;
  field: string;
  type: string;
}

interface EditingField {
  uuid: string;
  field: string;
}

interface DeleteConfirmState {
  open: boolean;
  uuid: string | null;
  name: string;
}

interface CallCardVehicleGridProps {
  vehicles: any[];
  title?: string;
  isReadOnly?: boolean;
  onChange?: (vehicles: any[]) => void;
  onLinkVehicle?: (uuid: string) => void;
  onShowLinkedVehicle?: (npsdVehicle: any) => void;
  onCreateNpsdVehicle?: (uuid: string, vehicle: any) => void;
}

const FIELDS: FieldInfo[] = [
  { label: 'Make', field: 'make', type: 'text' },
  { label: 'Model', field: 'model', type: 'text' },
  { label: 'Color', field: 'color', type: 'text' },
  { label: 'Year', field: 'year', type: 'text' },
  { label: 'Plate #', field: 'plateNumber', type: 'text' },
  { label: 'Plate State', field: 'plateState', type: 'text' },
];

export function CallCardVehicleGrid({
  vehicles,
  title = 'Vehicle Details',
  isReadOnly = false,
  onChange,
  onLinkVehicle,
  onShowLinkedVehicle,
  onCreateNpsdVehicle,
}: CallCardVehicleGridProps) {
  const { theme } = useTheme();

  const [editingField, setEditingField] = useState<EditingField | null>(null);
  const [editValue, setEditValue] = useState<any>('');
  const [isMobile, setIsMobile] = useState(false);
  const [isTabbing, setIsTabbing] = useState(false);
  const [justSelected, setJustSelected] = useState(false);
  const [isJustStartingEdit, setIsJustStartingEdit] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState>({
    open: false,
    uuid: null,
    name: '',
  });
  const [vehicleTypes, setVehicleTypes] = useState<any[]>([]);

  const containerRef = useRef<HTMLDivElement>(null);
  const editingFieldRef = useRef(editingField);
  const editValueRef = useRef(editValue);
  const isJustStartingEditRef = useRef(isJustStartingEdit);
  const vehiclesRef = useRef(vehicles);
  const vehicleTypesRef = useRef(vehicleTypes);
  const savedEditingFieldForTabRef = useRef<EditingField | null>(null);
  const justSelectedRef = useRef(justSelected);
  const pendingEditActionRef = useRef<string | null>(null);
  const shouldScrollToLastRef = useRef(false);

  // Keep refs in sync
  useEffect(() => { editingFieldRef.current = editingField; }, [editingField]);
  useEffect(() => { editValueRef.current = editValue; }, [editValue]);
  useEffect(() => { isJustStartingEditRef.current = isJustStartingEdit; }, [isJustStartingEdit]);
  useEffect(() => { vehiclesRef.current = vehicles; }, [vehicles]);
  useEffect(() => { vehicleTypesRef.current = vehicleTypes; }, [vehicleTypes]);
  useEffect(() => { justSelectedRef.current = justSelected; }, [justSelected]);

  // Mobile check (debounced to prevent re-render thrashing)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const checkIfMobile = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setIsMobile(window.innerWidth <= 1024), 150);
    };
    setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', checkIfMobile);
    return () => {
      window.removeEventListener('resize', checkIfMobile);
      if (timer) clearTimeout(timer);
    };
  }, []);

  // After vehicles array changes, scroll to last card if requested
  useEffect(() => {
    if (shouldScrollToLastRef.current) {
      shouldScrollToLastRef.current = false;
      if (containerRef.current) scrollToLastMobileCard(containerRef.current);
    }
  }, [vehicles]);

  // Load vehicle types on mount
  useEffect(() => {
    if (typeof Codes !== 'undefined' && typeof Codes.getVehicleTypes === 'function') {
      setVehicleTypes(Codes.getVehicleTypes());
    }
  }, []);

  const emitReplace = useCallback((newArr: any[]) => {
    onChange?.(newArr);
  }, [onChange]);

  const isEditing = useCallback((rowUuid: string, field: string): boolean => {
    return editingField?.uuid === rowUuid && editingField?.field === field;
  }, [editingField]);

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      if (!containerRef.current) return;
      const editContainer = containerRef.current.querySelector('.edit-container, .edit-field-mobile');
      if (editContainer) {
        const input = editContainer.querySelector('.edit-input:not([type="hidden"])') as HTMLInputElement;
        if (input) {
          if (isMobile) {
            setTimeout(() => { input.focus(); input.click(); }, 150);
          } else {
            input.focus();
            if (input.type === 'text') input.select();
          }
        }
      }
    });
  }, [isMobile]);

  // After editingField renders, perform any pending action (focus input)
  useEffect(() => {
    if (!editingField || !pendingEditActionRef.current) return;
    const action = pendingEditActionRef.current;
    pendingEditActionRef.current = null;

    if (action === 'focusInput') {
      focusInput();
    }
  }, [editingField, focusInput]);

  const saveEditDirect = useCallback(() => {
    const ef = editingFieldRef.current;
    if (!ef) return;
    if (isJustStartingEditRef.current) return;
    const { uuid, field } = ef;
    const val = editValueRef.current;

    if (field === 'vehicleType') {
      const selectedType = vehicleTypesRef.current.find((t: any) => t.code === val);
      const newArr = vehiclesRef.current.map((v: any) =>
        v.uuid === uuid ? { ...v, vehicleType: selectedType || null } : v
      );
      emitReplace(newArr);
    } else {
      const newArr = vehiclesRef.current.map((v: any) =>
        v.uuid === uuid ? { ...v, [field]: val } : v
      );
      emitReplace(newArr);
    }

    setEditingField(null);
    setEditValue('');
  }, [emitReplace]);

  const cancelEdit = useCallback(() => {
    setEditingField(null);
    setEditValue('');
    savedEditingFieldForTabRef.current = null;
  }, []);

  const getNextEditableField = useCallback((currentUuid: string, currentField: string) => {
    const editableFields = ['make', 'model', 'color', 'year', 'plateNumber', 'plateState', 'vehicleType', 'notes'];
    const currentVehicleIndex = vehiclesRef.current.findIndex((v: any) => v.uuid === currentUuid);
    const currentFieldIndex = editableFields.indexOf(currentField);
    const vehicle = vehiclesRef.current[currentVehicleIndex];
    const allowedFields = vehicle?.npsdVehicle ? ['notes'] : editableFields;
    for (let i = currentFieldIndex + 1; i < editableFields.length; i++) {
      if (allowedFields.indexOf(editableFields[i]) >= 0) {
        return { uuid: currentUuid, field: editableFields[i] };
      }
    }
    if (currentVehicleIndex < vehiclesRef.current.length - 1) {
      const nextVehicle = vehiclesRef.current[currentVehicleIndex + 1];
      const nextAllowedFields = nextVehicle?.npsdVehicle ? ['notes'] : editableFields;
      return { uuid: nextVehicle.uuid, field: nextAllowedFields[0] };
    }
    return null;
  }, []);

  const getPreviousEditableField = useCallback((currentUuid: string, currentField: string) => {
    const editableFields = ['make', 'model', 'color', 'year', 'plateNumber', 'plateState', 'vehicleType', 'notes'];
    const currentVehicleIndex = vehiclesRef.current.findIndex((v: any) => v.uuid === currentUuid);
    const currentFieldIndex = editableFields.indexOf(currentField);
    const vehicle = vehiclesRef.current[currentVehicleIndex];
    const allowedFields = vehicle?.npsdVehicle ? ['notes'] : editableFields;
    for (let i = currentFieldIndex - 1; i >= 0; i--) {
      if (allowedFields.indexOf(editableFields[i]) >= 0) {
        return { uuid: currentUuid, field: editableFields[i] };
      }
    }
    if (currentVehicleIndex > 0) {
      const prevVehicle = vehiclesRef.current[currentVehicleIndex - 1];
      const prevAllowedFields = prevVehicle?.npsdVehicle ? ['notes'] : editableFields;
      return { uuid: prevVehicle.uuid, field: prevAllowedFields[prevAllowedFields.length - 1] };
    }
    return null;
  }, []);

  const startEdit = useCallback((rowUuid: string, field: string, currentValue: any) => {
    if (isReadOnly) return;
    const vehicle = vehiclesRef.current.find((v: any) => v.uuid === rowUuid);
    if (vehicle?.npsdVehicle && field !== 'notes') return;

    if (editingFieldRef.current && (editingFieldRef.current.uuid !== rowUuid || editingFieldRef.current.field !== field)) {
      saveEditDirect();
    }

    setJustSelected(false);
    setEditingField({ uuid: rowUuid, field });

    if (field === 'vehicleType') {
      setEditValue(vehicle?.vehicleType?.code ?? null);
    } else {
      setEditValue(currentValue || '');
      pendingEditActionRef.current = 'focusInput';
    }
  }, [isReadOnly, saveEditDirect]);

  const startEditMobile = useCallback((rowUuid: string, field: string, currentValue: any) => {
    if (isReadOnly) return;
    const vehicle = vehiclesRef.current.find((v: any) => v.uuid === rowUuid);
    if (vehicle?.npsdVehicle && field !== 'notes') return;

    if (editingFieldRef.current && (editingFieldRef.current.uuid !== rowUuid || editingFieldRef.current.field !== field)) {
      saveEditDirect();
    }

    setIsJustStartingEdit(true);
    setJustSelected(false);
    setEditingField({ uuid: rowUuid, field });

    if (field === 'vehicleType') {
      setEditValue(vehicle?.vehicleType?.code ?? null);
      // isJustStartingEdit cleared on next tick via microtask
      Promise.resolve().then(() => setIsJustStartingEdit(false));
    } else {
      setEditValue(currentValue || '');
      setTimeout(() => { setIsJustStartingEdit(false); }, 200);
    }
  }, [isReadOnly, saveEditDirect]);

  const handleTabKey = useCallback((event: React.KeyboardEvent) => {
    if (!editingFieldRef.current) return;
    event.preventDefault();
    event.stopPropagation();

    setIsTabbing(true);
    const currentField = { ...editingFieldRef.current };
    const isShiftPressed = event.shiftKey;

    saveEditDirect();

    setTimeout(() => {
      const next = isShiftPressed
        ? getPreviousEditableField(currentField.uuid, currentField.field)
        : getNextEditableField(currentField.uuid, currentField.field);

      if (next) {
        const vehicle = vehiclesRef.current.find((v: any) => v.uuid === next.uuid);
        if (vehicle) {
          startEdit(next.uuid, next.field, vehicle[next.field]);
        }
      }
      setIsTabbing(false);
    }, 0);
  }, [saveEditDirect, getNextEditableField, getPreviousEditableField, startEdit]);

  const handleDropdownTab = useCallback((tabEvent: { shiftKey: boolean }) => {
    if (!savedEditingFieldForTabRef.current) return;
    const currentField = { ...savedEditingFieldForTabRef.current };
    savedEditingFieldForTabRef.current = null;

    setTimeout(() => {
      const next = tabEvent.shiftKey
        ? getPreviousEditableField(currentField.uuid, currentField.field)
        : getNextEditableField(currentField.uuid, currentField.field);

      if (next) {
        const vehicle = vehiclesRef.current.find((v: any) => v.uuid === next.uuid);
        if (vehicle) {
          startEdit(next.uuid, next.field, vehicle[next.field]);
        }
      }
    }, 0);
  }, [getPreviousEditableField, getNextEditableField, startEdit]);

  const handleVehicleTypeSelect = useCallback((value: string | number | null) => {
    if (editingFieldRef.current) {
      savedEditingFieldForTabRef.current = { ...editingFieldRef.current };
    }
    setJustSelected(true);
    // Save the vehicle type
    if (editingFieldRef.current) {
      const { uuid } = editingFieldRef.current;
      const selectedType = vehicleTypesRef.current.find((t: any) => t.code === value);
      const newArr = vehiclesRef.current.map((v: any) =>
        v.uuid === uuid ? { ...v, vehicleType: selectedType || null } : v
      );
      emitReplace(newArr);
    }
    setEditingField(null);
    setEditValue('');
  }, [emitReplace]);

  const handleVehicleTypeCancel = useCallback(() => {
    savedEditingFieldForTabRef.current = null;
    cancelEdit();
  }, [cancelEdit]);

  const handleDropdownClose = useCallback(() => {
    setTimeout(() => {
      if (editingFieldRef.current && editingFieldRef.current.field === 'vehicleType' && !justSelectedRef.current) {
        cancelEdit();
      }
      setJustSelected(false);
      if (!justSelectedRef.current) {
        savedEditingFieldForTabRef.current = null;
      }
    }, 50);
  }, [cancelEdit]);

  const forceSaveAndCloseAllEdits = useCallback(() => {
    if (editingFieldRef.current) {
      if (!isJustStartingEditRef.current) {
        saveEditDirect();
      }
      setEditingField(null);
      setEditValue('');
      savedEditingFieldForTabRef.current = null;
      setIsJustStartingEdit(false);
      setJustSelected(false);
    }
  }, [saveEditDirect]);

  // Expose forceSaveAndCloseAllEdits on window
  useEffect(() => {
    (window as any).CallCardVehicleGrid_forceSave = forceSaveAndCloseAllEdits;
    return () => { delete (window as any).CallCardVehicleGrid_forceSave; };
  }, [forceSaveAndCloseAllEdits]);

  const addVehicle = useCallback(() => {
    if (isReadOnly) {
      if (typeof App?.showPopupNotification === 'function') {
        App.showPopupNotification('Edit mode required.', 'warning');
      }
      return;
    }
    const newVehicle = {
      uuid: getInsecureUUID(),
      id: null,
      make: '',
      model: '',
      color: '',
      year: '',
      plateNumber: '',
      plateState: '',
      vehicleType: null,
      notes: '',
      npsdVehicle: null,
    };
    emitReplace([...vehicles, newVehicle]);
    shouldScrollToLastRef.current = true;
  }, [isReadOnly, vehicles, emitReplace]);

  const openDeleteConfirm = useCallback((uuid: string) => {
    if (isReadOnly) {
      if (typeof App?.showPopupNotification === 'function') {
        App.showPopupNotification('Edit mode required.', 'warning');
      }
      return;
    }
    const row = vehicles.find((v: any) => v.uuid === uuid);
    setDeleteConfirm({
      open: true,
      uuid,
      name: row ? `${row.make} ${row.model}` : 'this vehicle',
    });
  }, [isReadOnly, vehicles]);

  const cancelDeleteFn = useCallback(() => {
    setDeleteConfirm({ open: false, uuid: null, name: '' });
  }, []);

  const confirmDeleteFn = useCallback(() => {
    const uuid = deleteConfirm.uuid;
    if (!uuid) { cancelDeleteFn(); return; }
    emitReplace(vehicles.filter((v: any) => v.uuid !== uuid));
    cancelDeleteFn();
  }, [deleteConfirm.uuid, vehicles, emitReplace, cancelDeleteFn]);

  const linkToVehicle = useCallback((uuid: string) => {
    if (isReadOnly) {
      if (typeof App?.showPopupNotification === 'function') {
        App.showPopupNotification('Edit mode required.', 'warning');
      }
      return;
    }
    onLinkVehicle?.(uuid);
  }, [isReadOnly, onLinkVehicle]);

  const unlinkFromVehicle = useCallback((uuid: string) => {
    if (isReadOnly) {
      if (typeof App?.showPopupNotification === 'function') {
        App.showPopupNotification('Edit mode required.', 'warning');
      }
      return;
    }
    emitReplace(vehicles.map((v: any) => (v.uuid === uuid ? { ...v, npsdVehicle: null } : v)));
  }, [isReadOnly, vehicles, emitReplace]);

  const showLinkedVehicle = useCallback((uuid: string) => {
    const vehicle = vehicles.find((v: any) => v.uuid === uuid);
    if (vehicle?.npsdVehicle) {
      onShowLinkedVehicle?.(vehicle.npsdVehicle);
    }
  }, [vehicles, onShowLinkedVehicle]);

  const createNpsdVehicle = useCallback((uuid: string) => {
    if (isReadOnly) {
      if (typeof App?.showPopupNotification === 'function') {
        App.showPopupNotification('Edit mode required.', 'warning');
      }
      return;
    }
    const vehicle = vehicles.find((v: any) => v.uuid === uuid);
    if (!vehicle) return;
    if (!vehicle.make || !vehicle.model || !vehicle.plateNumber) {
      const missingFields: string[] = [];
      if (!vehicle.make) missingFields.push('make');
      if (!vehicle.model) missingFields.push('model');
      if (!vehicle.plateNumber) missingFields.push('plate number');
      const message = `Vehicle requires ${missingFields.join(', ')}.`;
      if (typeof App !== 'undefined' && typeof App.showPopupNotification === 'function') {
        App.showPopupNotification(message, 'error');
      }
      return;
    }
    onCreateNpsdVehicle?.(uuid, vehicle);
  }, [isReadOnly, vehicles, onCreateNpsdVehicle]);

  const getVehicleTypeDisplay = useCallback((vehicleType: any): string => {
    if (!vehicleType) return '\u2014';
    return vehicleType.value || vehicleType.code || '\u2014';
  }, []);

  const handleSaveEdit = useCallback(() => {
    saveEditDirect();
  }, [saveEditDirect]);

  const handleCancelEdit = useCallback(() => {
    cancelEdit();
  }, [cancelEdit]);

  return (
    <div className={`vehicles-assigned ${theme}`} ref={containerRef}>
      <div className="header">
        <h1>{title}</h1>
        <button
          className="btn-add"
          onClick={addVehicle}
          disabled={isReadOnly}
          title={isReadOnly ? 'Edit mode required' : 'Add vehicle'}
        >
          <i className="fas fa-plus"></i> VEHICLE
        </button>
      </div>

      {/* Desktop Table */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th style={{ width: '12%' }}>Make</th>
              <th style={{ width: '12%' }}>Model</th>
              <th style={{ width: '10%' }}>Color</th>
              <th style={{ width: '8%' }}>Year</th>
              <th style={{ width: '12%' }}>Plate #</th>
              <th style={{ width: '10%' }}>Plate State</th>
              <th style={{ width: '12%' }}>Type</th>
              <th style={{ width: '16%' }}>Notes</th>
              <th style={{ width: '10%' }}>Link</th>
              <th style={{ width: '8%' }}>Actions</th>
            </tr>
          </thead>
          {vehicles.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={10} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                  No vehicles assigned
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {vehicles.map((vehicle: any) => (
                <tr key={vehicle.uuid}>
                  {/* Text fields: make, model, color, year, plateNumber, plateState */}
                  {FIELDS.map((fieldInfo) => (
                    <td key={fieldInfo.field}>
                      {isEditing(vehicle.uuid, fieldInfo.field) ? (
                        <div className="edit-container">
                          <input
                            className="edit-input"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={handleSaveEdit}
                            onKeyUp={(e) => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') handleCancelEdit(); }}
                            onKeyDown={(e) => { if (e.key === 'Tab') { e.preventDefault(); handleTabKey(e); } }}
                          />
                        </div>
                      ) : (
                        <div
                          className={`${fieldInfo.field === 'make' ? (!isReadOnly ? 'editable' : '') + (vehicle.npsdVehicle ? ' linked' : '') : (!isReadOnly && !vehicle.npsdVehicle ? 'editable' : '')}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (fieldInfo.field === 'make') {
                              vehicle.npsdVehicle ? showLinkedVehicle(vehicle.uuid) : startEdit(vehicle.uuid, fieldInfo.field, vehicle[fieldInfo.field]);
                            } else {
                              if (!vehicle.npsdVehicle) startEdit(vehicle.uuid, fieldInfo.field, vehicle[fieldInfo.field]);
                            }
                          }}
                        >
                          {vehicle[fieldInfo.field] || '\u2014'}
                        </div>
                      )}
                    </td>
                  ))}

                  {/* Vehicle Type */}
                  <td>
                    {isEditing(vehicle.uuid, 'vehicleType') ? (
                      <div className="edit-container">
                        <Dropdownlist
                          value={editValue}
                          items={vehicleTypes}
                          labelKey="value"
                          valueKey="code"
                          placeholder="Select type..."
                          defaultOpen={!isMobile}
                          onChange={handleVehicleTypeSelect}
                          onCancel={handleVehicleTypeCancel}
                          onTab={handleDropdownTab}
                          onClose={handleDropdownClose}
                        />
                      </div>
                    ) : (
                      <div
                        className={`${!isReadOnly && !vehicle.npsdVehicle ? 'editable' : ''}`}
                        onClick={(e) => { e.stopPropagation(); if (!vehicle.npsdVehicle) startEdit(vehicle.uuid, 'vehicleType', vehicle.vehicleType); }}
                      >
                        {getVehicleTypeDisplay(vehicle.vehicleType)}
                      </div>
                    )}
                  </td>

                  {/* Notes */}
                  <td>
                    {isEditing(vehicle.uuid, 'notes') ? (
                      <div className="edit-container">
                        <input
                          className="edit-input"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={handleSaveEdit}
                          onKeyUp={(e) => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') handleCancelEdit(); }}
                          onKeyDown={(e) => { if (e.key === 'Tab') { e.preventDefault(); handleTabKey(e); } }}
                        />
                      </div>
                    ) : (
                      <div
                        className={!isReadOnly ? 'editable' : ''}
                        onClick={(e) => { e.stopPropagation(); startEdit(vehicle.uuid, 'notes', vehicle.notes); }}
                      >
                        {vehicle.notes || '\u2014'}
                      </div>
                    )}
                  </td>

                  {/* Link */}
                  <td className="link-cell">
                    {vehicle.npsdVehicle ? (
                      <button
                        className="btn-link-action"
                        onClick={() => unlinkFromVehicle(vehicle.uuid)}
                        disabled={isReadOnly}
                        title={isReadOnly ? 'Edit mode required' : 'Unlink vehicle'}
                      >
                        Unlink
                      </button>
                    ) : (
                      <>
                        <button
                          className="btn-link-action"
                          onClick={() => linkToVehicle(vehicle.uuid)}
                          disabled={isReadOnly}
                          title={isReadOnly ? 'Edit mode required' : 'Link vehicle'}
                        >
                          Link
                        </button>
                        <button
                          className="btn-link-action btn-create"
                          onClick={() => createNpsdVehicle(vehicle.uuid)}
                          disabled={isReadOnly}
                          title={isReadOnly ? 'Edit mode required' : 'Create vehicle'}
                        >
                          Create
                        </button>
                      </>
                    )}
                  </td>

                  {/* Actions */}
                  <td>
                    <div className="actions">
                      <button
                        className="btn-delete"
                        onClick={() => openDeleteConfirm(vehicle.uuid)}
                        disabled={isReadOnly}
                        title={isReadOnly ? 'Edit mode required' : 'Remove vehicle'}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="mobile-cards">
        {vehicles.length === 0 ? (
          <div className="empty-state">
            <p>No vehicles assigned</p>
          </div>
        ) : (
          vehicles.map((vehicle: any) => (
            <div key={'mobile-' + vehicle.uuid} className="card">
              <div className="card-header">
                <div className="card-header-content">
                  <div
                    className={`card-vehicle-name ${vehicle.npsdVehicle ? 'linked' : ''}`}
                    onClick={() => vehicle.npsdVehicle && showLinkedVehicle(vehicle.uuid)}
                  >
                    {vehicle.make} {vehicle.model} {vehicle.year}
                  </div>
                </div>
                <button
                  className="btn-delete"
                  onClick={() => openDeleteConfirm(vehicle.uuid)}
                  disabled={isReadOnly}
                  title={isReadOnly ? 'Edit mode required' : 'Remove vehicle'}
                >
                  Delete
                </button>
              </div>

              <div className="card-grid">
                {FIELDS.map((fieldInfo) => (
                  <div key={fieldInfo.field} className="card-field">
                    <div className="card-field-label">{fieldInfo.label}</div>
                    {isEditing(vehicle.uuid, fieldInfo.field) ? (
                      <div className="edit-field-mobile">
                        <input
                          className="edit-input"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          autoFocus
                          onBlur={handleSaveEdit}
                          onKeyUp={(e) => { if (e.key === 'Enter') handleSaveEdit(); }}
                        />
                      </div>
                    ) : (
                      <div
                        className={`card-field-value ${!isReadOnly && !vehicle.npsdVehicle ? 'editable' : ''}`}
                        onClick={() => {
                          if (!vehicle.npsdVehicle) startEditMobile(vehicle.uuid, fieldInfo.field, vehicle[fieldInfo.field]);
                        }}
                      >
                        {vehicle[fieldInfo.field] || '\u2014'}
                      </div>
                    )}
                  </div>
                ))}

                {/* Vehicle Type - mobile */}
                <div className="card-field">
                  <div className="card-field-label">Type</div>
                  {isEditing(vehicle.uuid, 'vehicleType') ? (
                    <div className="edit-field-mobile">
                      <Dropdownlist
                        value={editValue}
                        items={vehicleTypes}
                        labelKey="value"
                        valueKey="code"
                        placeholder="Select type..."
                        defaultOpen={isMobile}
                        onChange={handleVehicleTypeSelect}
                        onCancel={handleVehicleTypeCancel}
                        onTab={handleDropdownTab}
                        onClose={handleDropdownClose}
                      />
                    </div>
                  ) : (
                    <div
                      className={`card-field-value ${!isReadOnly && !vehicle.npsdVehicle ? 'editable' : ''}`}
                      onClick={() => { if (!vehicle.npsdVehicle) startEditMobile(vehicle.uuid, 'vehicleType', vehicle.vehicleType); }}
                    >
                      {getVehicleTypeDisplay(vehicle.vehicleType)}
                    </div>
                  )}
                </div>

                {/* Notes - mobile */}
                <div className="card-field card-field-notes">
                  <div className="card-field-label">Notes</div>
                  {isEditing(vehicle.uuid, 'notes') ? (
                    <div className="edit-field-mobile">
                      <textarea
                        className="edit-input"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        autoFocus
                        onBlur={handleSaveEdit}
                        rows={2}
                      />
                    </div>
                  ) : (
                    <div
                      className={`card-field-value ${!isReadOnly ? 'editable' : ''}`}
                      onClick={() => startEditMobile(vehicle.uuid, 'notes', vehicle.notes)}
                    >
                      {vehicle.notes || '\u2014'}
                    </div>
                  )}
                </div>
              </div>

              <div className="card-actions">
                {vehicle.npsdVehicle ? (
                  <button
                    className="btn-link-action-mobile"
                    onClick={() => unlinkFromVehicle(vehicle.uuid)}
                    disabled={isReadOnly}
                    title={isReadOnly ? 'Edit mode required' : 'Unlink vehicle'}
                  >
                    <i className="fas fa-unlink"></i> Unlink
                  </button>
                ) : (
                  <>
                    <button
                      className="btn-link-action-mobile"
                      onClick={() => linkToVehicle(vehicle.uuid)}
                      disabled={isReadOnly}
                      title={isReadOnly ? 'Edit mode required' : 'Link vehicle'}
                    >
                      <i className="fas fa-link"></i> Link
                    </button>
                    <button
                      className="btn-link-action-mobile btn-create-mobile"
                      onClick={() => createNpsdVehicle(vehicle.uuid)}
                      disabled={isReadOnly}
                      title={isReadOnly ? 'Edit mode required' : 'Create vehicle'}
                    >
                      <i className="fas fa-plus"></i> Create
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <ConfirmModal
        open={deleteConfirm.open}
        title={'Remove ' + deleteConfirm.name + '?'}
        confirmText="Remove"
        cancelText="Cancel"
        autoFocus="cancel"
        scrollLock={true}
        theme={theme}
        onConfirm={confirmDeleteFn}
        onCancel={cancelDeleteFn}
      />
    </div>
  );
}

(window as any).CallCardVehicleGridReact = CallCardVehicleGrid;
